import { useState } from 'react';
import { Box, Typography, Paper, CircularProgress, Card, CardContent, useMediaQuery, useTheme, Button } from '@mui/material';
import { ImageUploader } from '../components/ImageUploader';
import { PartCarousel } from '../components/PartCarousel';
import { SettingsDropdown } from '../components/SettingsDropdown';
import { MobileCameraBanner } from '../components/MobileCameraBanner';
import { PartInfo as PartInfoType } from '../types';
import { uploadImage } from '../services/api';
import axios from 'axios';
import React from 'react';
import { ArrowBack } from '@mui/icons-material';

export const ServicePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parts, setParts] = useState<PartInfoType[] | null>(null);
  // Default calibrated for the fine-tuned DINOv2 recognizer on the 149-image
  // hold-out set: correct top-1 cosine min≈0.605 / p5≈0.80 / median≈0.96.
  // 50 sits safely below the observed correct-match minimum so genuine matches
  // are never hidden, while still trimming sub-0.5 noise. (CLIP's old 70 was
  // on a different score scale and rejected nearly everything.) NOTE: right vs
  // wrong cosines overlap (0.80–0.98) for this model, so this gate cannot do
  // precision filtering — the true part is in top-5 100% of the time.
  const [threshold, setThreshold] = useState<number>(50);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [hasUploadedFirstImage, setHasUploadedFirstImage] = useState(false);
  const leftPanelWidth = isMobile ? 100 : 40; // Percentage

  const handleImageUpload = async (file: File) => {
    const imageUrl = URL.createObjectURL(file);
    setUploadedImage(imageUrl);
    setHasUploadedFirstImage(true);

    setLoading(true);
    setError(null);
    setParts(null);

    try {
      const response = await uploadImage(file);
      setParts(response);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const errorMessage = err.response?.data || err.message || 'An error occurred';
        setError(errorMessage);
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleThresholdChange = (newThreshold: number) => {
    setThreshold(newThreshold);
  };

  const handleBackToUpload = () => {
    setUploadedImage(null);
    setParts(null);
    setError(null);
    setHasUploadedFirstImage(false);
  };

  React.useEffect(() => {
    return () => {
      if (uploadedImage) {
        URL.revokeObjectURL(uploadedImage);
      }
    };
  }, [uploadedImage]);

  const filteredParts = parts ? parts.filter(part => part.similarity * 100.0 >= threshold) : null;
  const hasResults = parts && parts.length > 0;
  const hasFilteredResults = filteredParts && filteredParts.length > 0;

  const groupedParts = React.useMemo(() => {
    if (!filteredParts) return [];

    const grouped = filteredParts.reduce((acc, part) => {
      if (!acc[part.id]) {
        acc[part.id] = [];
      }
      acc[part.id].push(part);
      return acc;
    }, {} as Record<string, PartInfoType[]>);

    return Object.entries(grouped).map(([partId, variants]) => ({
      partId,
      variants: variants.sort((a, b) => {
        if (a.primaryOrOther !== b.primaryOrOther) {
          return a.primaryOrOther === 'Primary' ? -1 : 1;
        }
        return a.imageId.localeCompare(b.imageId);
      })
    }));
  }, [filteredParts]);

  return (
    <Box sx={{
      height: { xs: 'auto', md: '100vh' },
      display: 'flex',
      flexDirection: 'column',
      overflow: { xs: 'visible', md: 'hidden' },
      minHeight: { xs: '100vh', md: 'auto' }
    }}>
      {/* Header */}
      <Box sx={{
        p: { xs: 1.5, sm: 2 },
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        position: 'relative'
      }}>
        {/* Settings Button */}
        <Box sx={{ position: 'absolute', top: { xs: 12, sm: 16 }, right: { xs: 12, sm: 16 } }}>
          <SettingsDropdown
            threshold={threshold}
            onThresholdChange={handleThresholdChange}
            disabled={loading}
          />
        </Box>

        {/* Centered Title */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
          >
            VIPAR Parts Recognition
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Upload an image of a part to identify it
          </Typography>
        </Box>
      </Box>

      {isMobile && !hasUploadedFirstImage && (
        <Box sx={{ px: { xs: 1.5, sm: 2 } }}>
          <MobileCameraBanner isMobile={isMobile} />
        </Box>
      )}

      {/* Responsive Layout */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Left Panel - Image Upload & Preview */}
        <Paper
          elevation={0}
          sx={{
            width: `${leftPanelWidth}%`,
            display: 'flex',
            flexDirection: 'column',
            borderRight: { xs: 0, md: 1 },
            borderBottom: { xs: 1, md: 0 },
            borderColor: 'divider',
            overflow: 'hidden',
            minHeight: { xs: 'auto', md: '100%' },
            maxHeight: { xs: isMobile && uploadedImage ? '50vh' : 'none', md: 'none' }
          }}
        >


          <Box sx={{
            p: { xs: 2, sm: 3 },
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            gap: { xs: 2, sm: 3 }
          }}>
            {/* Upload Section */}
            <Box sx={{ flexShrink: 0 }}>
              {!hasUploadedFirstImage && (
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                >
                  Upload Image
                </Typography>
              )}
              <ImageUploader onUpload={handleImageUpload} compact={hasUploadedFirstImage} />
            </Box>
            {/* Image Preview Section */}
            {uploadedImage && (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                height: { xs: '250px', sm: '300px' },
                flexShrink: 0
              }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                >
                  Preview
                </Typography>
                <Box sx={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  overflow: 'hidden',
                  position: 'relative',
                  minHeight: 0
                }}>
                  <img
                    src={uploadedImage}
                    alt="Uploaded part"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Divider - Only show on desktop */}
        {!isMobile && (
          <Box
            sx={{
              width: 1.5,
              bgcolor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s ease'
            }}
          >
          </Box>
        )}

        {/* Right Panel - Results */}
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <Box sx={{
            flex: 1,
            overflow: { xs: 'visible', md: 'auto' },
            p: { xs: 2, sm: 3 }
          }}>
            {/* Loading State */}
            {loading && (
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 200
              }}>
                <CircularProgress />
              </Box>
            )}

            {/* Error Message */}
            {error && (
              <Paper sx={{ 
                p: 2, 
                bgcolor: 'error.light',
                color: 'error.contrastText',
                textAlign: 'center',
                mb: 2
              }}>
                {error}
              </Paper>
            )}

            {/* Results Display */}
            {hasResults && hasFilteredResults && (
              <>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3, 
                    mb: 4, 
                    bgcolor: 'grey.50',
                    borderLeft: 4,
                    borderLeftColor: 'action.active'
                  }}
                >
                  {/* Count Information */}
                  <Box sx={{
                    display: 'flex',
                    gap: { xs: 1, sm: 2 },
                    flexWrap: 'wrap',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' }
                  }}>
                    <Typography
                      variant="h6"
                      color="text.primary"
                      sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                    >
                      Results: {groupedParts.length} similar parts
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                    >
                      ({filteredParts.length} variants)
                    </Typography>
                  </Box>
                </Paper>

                {groupedParts.map((group) => (
                  <Box key={group.partId} sx={{ mb: 4 }}>
                    <PartCarousel
                      parts={group.variants}
                    />
                  </Box>
                ))}
              </>
            )}

            {/* No filtered results message */}
            {hasResults && !hasFilteredResults && (
              <Card>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{
                    textAlign: 'center'
                  }}>
                    <Typography
                      variant="h5"
                      component="h2"
                      gutterBottom
                      color="warning.main"
                      sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                    >
                      No Parts Meet the Threshold
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {parts.length} part{parts.length !== 1 ? 's were' : ' was'} found, but none meet the {threshold}% similarity threshold.
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      Try lowering the threshold to see more results, or upload a different image.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* No parts found message */}
            {parts && parts.length === 0 && (
              <Card>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{
                    textAlign: 'center'
                  }}>
                    <Typography
                      variant="h4"
                      component="h1"
                      gutterBottom
                      color="error.main"
                      sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
                    >
                      No Parts Found!
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      Try lowering the threshold or uploading a different image.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Default message when no image uploaded */}
            {!uploadedImage && !loading && !error && (
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: { xs: '200px', sm: '100%' },
                textAlign: 'center',
                px: { xs: 2, sm: 0 }
              }}>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  Upload an image to see recognition results
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
